import { useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Pencil, Trash2, Plus, X, Check } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

const TUR_OPTIONS = [
  { value: 'meditasyon', label: 'Meditasyon' },
  { value: 'nefes', label: 'Nefes' },
  { value: 'frekans', label: 'Frekans' },
  { value: 'diyet', label: 'Diyet' },
  { value: 'rituel', label: 'Ritüel' },
  { value: 'genel', label: 'Genel' },
];

type Kategori = {
  id: string;
  isim: string;
  isim_en?: string;
  isim_es?: string;
  handle: string;
  tur: string;
  sira: number;
  created_at?: string;
};

type FormState = { isim: string; isim_en: string; isim_es: string; handle: string; tur: string; sira: string };
const EMPTY_FORM: FormState = { isim: '', isim_en: '', isim_es: '', handle: '', tur: 'meditasyon', sira: '0' };

async function fetchKategoriler(tur?: string): Promise<{ ok: boolean; items: Kategori[] }> {
  const url = `/api/admin/kategoriler${tur ? `?tur=${encodeURIComponent(tur)}` : ''}`;
  const res = await adminFetch(url);
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? 'Kategoriler alınamadı.');
  return json;
}

async function createKategori(body: FormState) {
  const res = await adminFetch('/api/admin/kategoriler', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, sira: Number(body.sira) }),
  });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? 'Eklenemedi.');
  return json;
}

async function updateKategori(id: string, body: Partial<FormState>) {
  const res = await adminFetch(`/api/admin/kategoriler/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, ...(body.sira !== undefined ? { sira: Number(body.sira) } : {}) }),
  });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? 'Güncellenemedi.');
  return json;
}

async function deleteKategori(id: string) {
  const res = await adminFetch(`/api/admin/kategoriler/${id}`, { method: 'DELETE' });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? 'Silinemedi.');
  return json;
}

const baseInput =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-700 dark:bg-slate-900";

export default function AdminKategoriler() {
  const queryClient = useQueryClient();
  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [filterTur, setFilterTur] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);

  const qKey = ['admin-kategoriler', filterTur];
  const listQuery = useQuery({
    queryKey: qKey,
    queryFn: () => fetchKategoriler(filterTur || undefined),
    enabled: authenticated,
    staleTime: 10_000,
  });

  const createMutation = useMutation({
    mutationFn: createKategori,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-kategoriler'] });
      setForm(EMPTY_FORM);
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<FormState> }) => updateKategori(id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-kategoriler'] });
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteKategori,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-kategoriler'] });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground p-8">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const items = listQuery.data?.items ?? [];

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                Kategori Yönetimi
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 space-y-6 p-4 sm:p-6">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              value={filterTur}
              onChange={(e) => setFilterTur(e.target.value)}
            >
              <option value="">Tüm türler</option>
              {TUR_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Button
              className="rounded-full gap-1.5"
              size="sm"
              onClick={() => { setShowForm(true); setForm(EMPTY_FORM); }}
            >
              <Plus className="h-4 w-4" />
              Yeni Kategori
            </Button>
          </div>

          {/* Yeni kategori formu */}
          {showForm && (
            <section className="rounded-3xl border border-purple-200 bg-purple-50/50 p-5 dark:border-purple-800/40 dark:bg-purple-900/10">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Yeni Kategori</h3>
                <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">🇹🇷 İsim (TR)</label>
                  <input className={baseInput + " mt-1"} value={form.isim} onChange={e => setForm(f => ({ ...f, isim: e.target.value }))} placeholder="Çakra" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">🇬🇧 İsim EN</label>
                  <input className={baseInput + " mt-1"} value={form.isim_en} onChange={e => setForm(f => ({ ...f, isim_en: e.target.value }))} placeholder="Chakra" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">🇪🇸 İsim ES</label>
                  <input className={baseInput + " mt-1"} value={form.isim_es} onChange={e => setForm(f => ({ ...f, isim_es: e.target.value }))} placeholder="Chakra" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Handle</label>
                  <input className={baseInput + " mt-1"} value={form.handle} onChange={e => setForm(f => ({ ...f, handle: e.target.value }))} placeholder="cakra" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Tür</label>
                  <select className={baseInput + " mt-1"} value={form.tur} onChange={e => setForm(f => ({ ...f, tur: e.target.value }))}>
                    {TUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Sıra</label>
                  <input type="number" min={0} className={baseInput + " mt-1"} value={form.sira} onChange={e => setForm(f => ({ ...f, sira: e.target.value }))} />
                </div>
              </div>
              {createMutation.isError && (
                <p className="mt-3 text-xs text-red-600">{(createMutation.error as Error).message}</p>
              )}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowForm(false)}>İptal</Button>
                <Button size="sm" className="rounded-full" onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Ekleniyor...' : 'Ekle'}
                </Button>
              </div>
            </section>
          )}

          {/* Liste */}
          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            {listQuery.isLoading ? (
              <div className="p-6 text-sm text-slate-500">Yükleniyor...</div>
            ) : listQuery.isError ? (
              <div className="p-6 text-sm text-red-600">{(listQuery.error as Error).message}</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-sm text-slate-400">Kategori bulunamadı.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">İsim</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Handle</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Tür</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Sıra</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id} className="border-b border-slate-50 last:border-0 dark:border-slate-800/60">
                        {editId === item.id ? (
                          <>
                            <td className="px-4 py-2">
                              <div className="grid gap-1">
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400 w-5">🇹🇷</span>
                                  <input className={baseInput} value={editForm.isim} onChange={e => setEditForm(f => ({ ...f, isim: e.target.value }))} />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400 w-5">🇬🇧</span>
                                  <input className={baseInput} placeholder="EN" value={editForm.isim_en} onChange={e => setEditForm(f => ({ ...f, isim_en: e.target.value }))} />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400 w-5">🇪🇸</span>
                                  <input className={baseInput} placeholder="ES" value={editForm.isim_es} onChange={e => setEditForm(f => ({ ...f, isim_es: e.target.value }))} />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <input className={baseInput} value={editForm.handle} onChange={e => setEditForm(f => ({ ...f, handle: e.target.value }))} />
                            </td>
                            <td className="px-4 py-2">
                              <select className={baseInput} value={editForm.tur} onChange={e => setEditForm(f => ({ ...f, tur: e.target.value }))}>
                                {TUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <input type="number" min={0} className={baseInput} style={{ width: 70 }} value={editForm.sira} onChange={e => setEditForm(f => ({ ...f, sira: e.target.value }))} />
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="text-green-600 hover:text-green-800"
                                  onClick={() => updateMutation.mutate({ id: item.id, body: editForm })}
                                  disabled={updateMutation.isPending}
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setEditId(null)}>
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800 dark:text-slate-100">{item.isim}</div>
                              {(item.isim_en || item.isim_es) && (
                                <div className="mt-0.5 flex gap-1.5">
                                  {item.isim_en && <span className="text-[10px] text-slate-400">🇬🇧 {item.isim_en}</span>}
                                  {item.isim_es && <span className="text-[10px] text-slate-400">🇪🇸 {item.isim_es}</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-500 font-mono text-xs">{item.handle}</td>
                            <td className="px-4 py-3">
                              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                                {item.tur}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500">{item.sira}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="text-slate-400 hover:text-blue-600"
                                  onClick={() => {
                                    setEditId(item.id);
                                    setEditForm({ isim: item.isim, isim_en: item.isim_en ?? '', isim_es: item.isim_es ?? '', handle: item.handle, tur: item.tur, sira: String(item.sira) });
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  className="text-slate-400 hover:text-red-600"
                                  onClick={() => { if (confirm('Silinsin mi?')) deleteMutation.mutate(item.id); }}
                                  disabled={deleteMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
